import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
  UploadCloud, Users, ArrowRight, Plus, Trash2, CheckCircle2,
  AlertCircle, Loader2, FileText, Download, UserPlus, UserMinus, Repeat, Link2,
  Cloud, RefreshCw
} from "lucide-react";

/**
 * Panel de sincronización de personal (SAP HR CSV).
 * - Pestaña "Importar": subir CSV, ver diff y aplicar
 * - Pestaña "Mapeo de códigos": gestionar equivalencia código CSV → contrato
 */
export default function PersonnelSyncTab({ contracts }) {
  const { authAxios } = useAuth();
  const [subTab, setSubTab] = useState("import"); // 'import' | 'mapping'

  return (
    <div className="space-y-5 animate-fade-in-up">
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
        {[
          { id: "import", label: "Importar CSV", icon: UploadCloud },
          { id: "mapping", label: "Mapeo de códigos", icon: Link2 },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`sync-subtab-${id}`}
            onClick={() => setSubTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              subTab === id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {subTab === "import" && <ImportTab contracts={contracts} authAxios={authAxios} />}
      {subTab === "mapping" && <MappingTab contracts={contracts} authAxios={authAxios} />}
    </div>
  );
}

/* ═════════════════════════════  IMPORT TAB  ═════════════════════════════ */
function ImportTab({ contracts, authAxios }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null); // response from upload
  const [error, setError] = useState("");
  const [applyOpts, setApplyOpts] = useState({ apply_additions: true, apply_removals: true, apply_moves: true });
  const [successMsg, setSuccessMsg] = useState("");
  const [syncs, setSyncs] = useState([]);
  const [scInfo, setScInfo] = useState(null);
  const [scLoading, setScLoading] = useState(false);
  const [scFetching, setScFetching] = useState(false);

  const contractName = (id) => contracts.find((c) => c.id === id)?.name || id;

  const loadSyncs = async () => {
    try {
      const r = await authAxios.get("/admin/personnel/syncs");
      setSyncs(r.data || []);
    } catch {}
  };

  const loadScInfo = async () => {
    setScLoading(true);
    try {
      const r = await authAxios.get("/admin/personnel/sync/smartclockin/info");
      setScInfo(r.data);
    } catch (e) {
      setScInfo({ available: false, error: e.response?.data?.detail || "No disponible" });
    } finally {
      setScLoading(false);
    }
  };

  useEffect(() => { loadSyncs(); loadScInfo(); }, []);

  const handleSmartClockInFetch = async () => {
    setError(""); setSuccessMsg(""); setResult(null); setScFetching(true);
    try {
      const r = await authAxios.post("/admin/personnel/sync/smartclockin/fetch");
      setResult(r.data);
      loadSyncs();
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo descargar desde SmartClockIn");
    } finally {
      setScFetching(false);
    }
  };

  const handleFile = (f) => {
    setError(""); setSuccessMsg("");
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setError("El archivo debe tener extensión .csv");
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setError(""); setSuccessMsg(""); setResult(null); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await authAxios.post("/admin/personnel/sync/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      loadSyncs();
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo procesar el CSV");
    } finally {
      setUploading(false);
    }
  };

  const handleApply = async () => {
    if (!result?.sync_id) return;
    if (!window.confirm("¿Aplicar los cambios seleccionados sobre el personal?")) return;
    setApplying(true); setError("");
    try {
      const r = await authAxios.post(`/admin/personnel/sync/${result.sync_id}/apply`, applyOpts);
      setSuccessMsg(`Cambios aplicados: +${r.data.stats.added} altas · ↺${r.data.stats.reactivated} reactivados · ↔${r.data.stats.moved} movidos · −${r.data.stats.removed} bajas`);
      setResult(null);
      setFile(null);
      loadSyncs();
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo aplicar la sincronización");
    } finally {
      setApplying(false);
    }
  };

  const handleDiscard = () => {
    setResult(null); setFile(null); setError(""); setSuccessMsg("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const total = result?.totals;

  return (
    <div className="space-y-5">
      <div className="card-white p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#E6F4EA] rounded-xl flex items-center justify-center">
            <Users size={20} className="text-[#007934]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Sincronización de personal (SAP HR)</h2>
            <p className="text-sm text-slate-500">Sube el CSV diario de Loga3 (cp850, separador `;`). Los cambios se muestran antes de aplicar.</p>
          </div>
        </div>

        {error && (
          <div data-testid="sync-error" className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm flex items-center gap-2">
            <AlertCircle size={16} /> {error}
          </div>
        )}
        {successMsg && (
          <div data-testid="sync-success" className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2">
            <CheckCircle2 size={16} /> {successMsg}
          </div>
        )}

        {!result && (
          <>
            {/* SmartClockIn remote feed */}
            <div className="mb-5 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white p-4" data-testid="smartclockin-panel">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Cloud size={18} className="text-emerald-700" />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-bold text-slate-900">SmartClockIn · feed automático</p>
                  {scLoading ? (
                    <p className="text-xs text-slate-500 mt-0.5">Comprobando disponibilidad...</p>
                  ) : scInfo?.available ? (
                    <p className="text-xs text-slate-600 mt-0.5">
                      <span className="font-mono text-slate-700">{scInfo.filename}</span> · {scInfo.n_rows?.toLocaleString("es-ES")} filas ·
                      origen <b>{scInfo.source}</b> · fecha <b>{scInfo.source_date}</b>
                    </p>
                  ) : (
                    <p className="text-xs text-amber-700 mt-0.5">
                      {scInfo?.error || "Sin CSV disponible de momento."}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={loadScInfo}
                    disabled={scLoading}
                    data-testid="smartclockin-refresh-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-60"
                    title="Actualizar estado"
                  >
                    <RefreshCw size={12} className={scLoading ? "animate-spin" : ""} />
                  </button>
                  <button
                    onClick={handleSmartClockInFetch}
                    disabled={scFetching || !scInfo?.available}
                    data-testid="smartclockin-fetch-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {scFetching ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                    {scFetching ? "Descargando..." : "Descargar y analizar"}
                  </button>
                </div>
              </div>
            </div>

            <div className="text-center text-xs text-slate-400 uppercase tracking-widest font-bold mb-3">
              — o subida manual —
            </div>

            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
              className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-[#007934] transition-colors"
            >
            <UploadCloud size={36} className="mx-auto text-slate-400 mb-3" />
            <p className="text-sm font-semibold text-slate-700 mb-1">
              Arrastra aquí el CSV de Loga3 (SCOUTD_*.csv)
            </p>
            <p className="text-xs text-slate-500 mb-4">o</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="sync-file-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              data-testid="sync-select-file-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#007934] hover:bg-[#005c27] text-white rounded-lg text-sm font-semibold transition-colors"
            >
              <FileText size={14} /> Seleccionar archivo
            </button>
            {file && (
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-700">
                <FileText size={14} />
                <span data-testid="sync-selected-file" className="font-mono">{file.name}</span>
                <span className="text-slate-400">({(file.size / 1024).toFixed(0)} KB)</span>
              </div>
            )}
            {file && (
              <button
                onClick={handleUpload}
                disabled={uploading}
                data-testid="sync-upload-btn"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-[#007934] hover:bg-[#005c27] text-white rounded-lg text-sm font-bold transition-colors disabled:opacity-60"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                {uploading ? "Analizando..." : "Analizar cambios"}
              </button>
            )}
            </div>
          </>
        )}

        {/* Result / diff preview */}
        {result && total && (
          <div className="space-y-5" data-testid="sync-result-panel">
            {/* KPI summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard icon={UserPlus} label="Altas" value={total.additions} color="emerald" testid="kpi-additions" />
              <KpiCard icon={Repeat} label="Cambios de centro" value={total.moves} color="amber" testid="kpi-moves" />
              <KpiCard icon={UserMinus} label="Bajas" value={total.removals} color="rose" testid="kpi-removals" />
              <KpiCard icon={CheckCircle2} label="Sin cambios" value={total.unchanged} color="slate" testid="kpi-unchanged" />
            </div>

            {/* Per-contract breakdown */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="bg-slate-50 px-4 py-2.5 text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center justify-between">
                <span>Detalle por contrato</span>
                <span className="font-mono text-slate-400">{result.mapped_rows.toLocaleString()} filas mapeadas / {result.row_count.toLocaleString()} totales</span>
              </div>
              <div className="divide-y divide-slate-100">
                {result.per_contract.map((c) => (
                  <ContractDiffRow key={c.contract_id} c={c} contractName={contractName} />
                ))}
              </div>
            </div>

            {/* Unmapped codes */}
            {result.unmapped_codes && result.unmapped_codes.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded-xl p-4">
                <p className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-1.5">
                  <AlertCircle size={14} /> Códigos sin mapear ({result.unmapped_codes.length})
                </p>
                <p className="text-xs text-amber-700 mb-2">Estos centros del CSV no están asociados a ningún contrato. Añádelos en "Mapeo de códigos" si te interesan.</p>
                <div className="max-h-40 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-amber-900 text-left">
                        <th className="py-1 pr-3">Código</th><th className="py-1 pr-3">Descripción</th><th className="py-1 text-right">Empleados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.unmapped_codes.slice(0, 20).map((u) => (
                        <tr key={u.csv_code} className="text-amber-900/90">
                          <td className="py-1 pr-3 font-mono">{u.csv_code}</td>
                          <td className="py-1 pr-3">{u.desc}</td>
                          <td className="py-1 text-right font-mono">{u.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Apply controls */}
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={applyOpts.apply_additions}
                    onChange={(e) => setApplyOpts((o) => ({ ...o, apply_additions: e.target.checked }))}
                    data-testid="sync-apply-additions" />
                  Aplicar altas
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={applyOpts.apply_moves}
                    onChange={(e) => setApplyOpts((o) => ({ ...o, apply_moves: e.target.checked }))}
                    data-testid="sync-apply-moves" />
                  Aplicar cambios de centro
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={applyOpts.apply_removals}
                    onChange={(e) => setApplyOpts((o) => ({ ...o, apply_removals: e.target.checked }))}
                    data-testid="sync-apply-removals" />
                  Marcar bajas como inactivos
                </label>
              </div>
              <div className="flex gap-2">
                <button onClick={handleDiscard} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900">
                  Descartar
                </button>
                <button
                  onClick={handleApply}
                  disabled={applying}
                  data-testid="sync-apply-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#007934] hover:bg-[#005c27] text-white rounded-lg text-sm font-bold transition-colors disabled:opacity-60"
                >
                  {applying ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  {applying ? "Aplicando..." : "Aplicar cambios"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent syncs log */}
      {syncs.length > 0 && (
        <div className="card-white p-6">
          <h3 className="text-base font-bold text-slate-900 mb-3">Sincronizaciones recientes</h3>
          <div className="divide-y divide-slate-100">
            {syncs.slice(0, 10).map((s) => (
              <div key={s.id} className="py-2.5 flex items-center justify-between text-sm">
                <div>
                  <p className="font-mono text-xs text-slate-500">{(s.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  <p className="text-slate-700">{s.filename}</p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  {s.status === "applied" && s.apply_stats ? (
                    <span className="text-emerald-700 font-semibold">
                      +{s.apply_stats.added} / ↺{s.apply_stats.reactivated} / ↔{s.apply_stats.moved} / −{s.apply_stats.removed}
                    </span>
                  ) : (
                    <span className="text-slate-500">
                      +{s.totals?.additions || 0} / ↔{s.totals?.moves || 0} / −{s.totals?.removals || 0}
                    </span>
                  )}
                  <StatusBadge status={s.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, testid }) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div data-testid={testid} className={`rounded-xl border p-4 ${colors[color]}`}>
      <Icon size={18} className="mb-1.5 opacity-80" />
      <p className="text-2xl font-extrabold tabular-nums">{value.toLocaleString("es-ES")}</p>
      <p className="text-xs font-semibold opacity-80">{label}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "Pendiente", cls: "bg-amber-100 text-amber-700" },
    applied: { label: "Aplicado", cls: "bg-emerald-100 text-emerald-700" },
  };
  const s = map[status] || { label: status || "-", cls: "bg-slate-100 text-slate-600" };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.cls}`}>{s.label}</span>;
}

function ContractDiffRow({ c, contractName }) {
  const [open, setOpen] = useState(false);
  const total = c.additions.length + c.removals.length + c.moves_in.length;
  return (
    <div className="px-4 py-2.5">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-mono text-xs text-slate-400 w-16">{c.contract_code || "—"}</span>
          <span className="text-sm font-semibold text-slate-800 truncate">{c.contract_name}</span>
        </div>
        <div className="flex items-center gap-2 text-xs whitespace-nowrap">
          {c.additions.length > 0 && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">+{c.additions.length}</span>}
          {c.moves_in.length > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold">↔{c.moves_in.length}</span>}
          {c.removals.length > 0 && <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 font-semibold">−{c.removals.length}</span>}
          {c.unchanged > 0 && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">={c.unchanged}</span>}
          {total === 0 && c.unchanged === 0 && <span className="text-slate-400">sin cambios</span>}
        </div>
      </button>
      {open && total > 0 && (
        <div className="mt-3 space-y-3">
          {c.additions.length > 0 && (
            <DetailList title="Altas" color="emerald" items={c.additions.map(p => ({ pnr: p.pnr, name: p.full_name, dni: p.dni }))} />
          )}
          {c.moves_in.length > 0 && (
            <DetailList title="Entradas desde otros contratos" color="amber" items={c.moves_in.map(p => ({
              pnr: p.pnr, name: p.full_name, dni: p.dni,
              meta: `de ${contractName(p.from_contract_id)}`,
            }))} />
          )}
          {c.removals.length > 0 && (
            <DetailList title="Bajas (dejarán de estar activos)" color="rose" items={c.removals.map(p => ({ pnr: p.pnr, name: p.full_name, dni: p.dni }))} />
          )}
        </div>
      )}
    </div>
  );
}

function DetailList({ title, color, items }) {
  const [showAll, setShowAll] = useState(false);
  const colorMap = { emerald: "text-emerald-700", amber: "text-amber-700", rose: "text-rose-700" };
  const shown = showAll ? items : items.slice(0, 5);
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${colorMap[color]}`}>
        {title} · {items.length}
      </p>
      <ul className="space-y-1 text-xs">
        {shown.map((it) => (
          <li key={it.pnr} className="flex items-center gap-3">
            <span className="font-mono text-slate-400 w-14">{it.pnr}</span>
            <span className="text-slate-700">{it.name}</span>
            {it.dni && <span className="font-mono text-slate-400">{it.dni}</span>}
            {it.meta && <span className="text-slate-500 italic">· {it.meta}</span>}
          </li>
        ))}
      </ul>
      {items.length > 5 && (
        <button onClick={() => setShowAll(!showAll)} className="mt-2 text-[11px] font-semibold text-slate-500 hover:text-slate-700">
          {showAll ? "Ocultar" : `Mostrar los ${items.length - 5} restantes...`}
        </button>
      )}
    </div>
  );
}

/* ═════════════════════════════  MAPPING TAB  ════════════════════════════ */
function MappingTab({ contracts, authAxios }) {
  const [mappings, setMappings] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [newContract, setNewContract] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const r = await authAxios.get("/admin/code-mappings");
      setMappings(r.data || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError("");
    if (!newCode.trim() || !newContract) {
      setError("Código y contrato obligatorios");
      return;
    }
    setLoading(true);
    try {
      await authAxios.post("/admin/code-mappings", {
        csv_code: newCode.trim(),
        contract_id: newContract,
      });
      setNewCode(""); setNewContract("");
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo guardar el mapeo");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (csv_code) => {
    if (!window.confirm(`¿Eliminar mapeo del código "${csv_code}"?`)) return;
    try {
      await authAxios.delete(`/admin/code-mappings/${encodeURIComponent(csv_code)}`);
      await load();
    } catch {}
  };

  const contractName = (id) => contracts.find((c) => c.id === id)?.name || id;
  const contractCode = (id) => contracts.find((c) => c.id === id)?.code || "";

  return (
    <div className="space-y-5">
      <div className="card-white p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#E6F4EA] rounded-xl flex items-center justify-center">
            <Link2 size={20} className="text-[#007934]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Mapeo de códigos de centro (CSV → Contrato)</h2>
            <p className="text-sm text-slate-500">Define la equivalencia entre el CODIGO CENTRO DE TRABAJO del SAP HR y los contratos de la plataforma.</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">{error}</div>
        )}

        <form onSubmit={handleAdd} className="flex flex-wrap gap-2 mb-5 items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-[11px] font-bold text-slate-500 mb-1 uppercase tracking-wide">Código CSV</label>
            <input
              data-testid="mapping-new-code"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              placeholder="ej: 897 o 00897"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#007934]"
            />
          </div>
          <div className="flex-[2] min-w-[200px]">
            <label className="block text-[11px] font-bold text-slate-500 mb-1 uppercase tracking-wide">Contrato</label>
            <select
              data-testid="mapping-new-contract"
              value={newContract}
              onChange={(e) => setNewContract(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#007934] bg-white"
            >
              <option value="">Selecciona contrato...</option>
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>{c.code ? `${c.code} — ` : ""}{c.name}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="mapping-add-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#007934] hover:bg-[#005c27] text-white rounded-lg text-sm font-bold disabled:opacity-60"
          >
            <Plus size={14} /> Añadir
          </button>
        </form>

        {mappings.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Todavía no hay mapeos definidos.</p>
        ) : (
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2.5">Código CSV</th>
                  <th className="px-4 py-2.5">Contrato</th>
                  <th className="px-4 py-2.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {mappings.map((m) => (
                  <tr key={m.csv_code} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-slate-700">{m.csv_code}</td>
                    <td className="px-4 py-2">
                      <span className="font-mono text-xs text-slate-400 mr-2">{contractCode(m.contract_id) || "—"}</span>
                      <span className="text-slate-800">{contractName(m.contract_id)}</span>
                      {m.auto_seeded && <span className="ml-2 text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">auto</span>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => handleDelete(m.csv_code)}
                        data-testid={`mapping-delete-${m.csv_code}`}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Eliminar mapeo"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
